<template>
  <v-alert class="ma-3" :max-width="maxWidth" :icon="false">
    <h2 class="mb-4">
      {{ label }}

      <v-icon v-if="file" class="cursor-pointer float-right" @click="resetForm">
        mdi-close
      </v-icon>
    </h2>
    <v-card-text class="pa-4">
      <div
        ref="dropzoneField"
        class="dropbox"
      >
        <div :class="outlineColor">
          <div v-if="file">
            <div
              v-if="isFileSet && !isError"
              class="text-center background-light success--text px-2 py-4"
            >
              <v-icon size="40" color="success">
                mdi-file-outline
              </v-icon>
              <p
                class="font-weight-medium text-center subtitle-2 mb-0 file-name"
                v-text="file.name"
              />
            </div>
            <div v-else class="text-center background-light error--text px-2 py-4">
              <v-icon size="40" color="error">
                mdi-file-outline
              </v-icon>
              <p class="font-weight-medium text-center subtitle-2 mb-0" v-text="errorMessage" />
            </div>
          </div>
          <div
            v-else
            class="text-center cursor-pointer background-light pt-8"
          >
            <input
              v-show="false"
              ref="fileInput"
              :accept="allowedFormats.map((item) => `.${item.toLowerCase()}`).join(',')"
              type="file"
              @change="uploadFileChanged"
            >
            <v-icon size="40">
              mdi-file-upload-outline
            </v-icon>
            <p class="font-weight-medium text-center subtitle-2 my-2">
              Klicken oder Ziehen und Ablegen
            </p>
            <p class="text-right caption pa-2 pr-4 primary--text">
              {{ allowedFormats.join(', ').toLowerCase() }}
            </p>
          </div>
        </div>
      </div>
      <v-btn
        class="mt-4"
        block
        variant="tonal"
        :disabled="downloadingOnProcess"
        @click="downloadFile()"
      >
        <template v-if="downloadingOnProcess">
          <v-icon right dark class="mr-2 loading-spinner">
            mdi-loading
          </v-icon>
          Wird Heruntergeladen...
        </template>
        <template v-else>
          <v-icon right dark class="mr-2">
            mdi-cloud-download
          </v-icon>
          Herunterladen
        </template>
      </v-btn>
    </v-card-text>
  </v-alert>
</template>
<script lang="ts" setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

const allowedFormats: string[] = ['PDF', 'PNG', 'JPG', 'JPEG', 'DOC', 'DOCX', 'TXT'];
const allowedFormatsRegex: RegExp = /(.*?).(png|jpg|jpeg|pdf|doc|docx|txt)$/;

const {
  label = 'Vertrag',
  maxWidth = 750,
} = defineProps<{
  label?: string;
  maxWidth?: number;
}>();

const emit = defineEmits<{
  'file-set': [value: File | undefined];
  'download-file': [];
}>();

const dropzoneField = ref<HTMLDivElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

const file = ref<File>();

const errorMessage = ref<string>();
const downloadingOnProcess = ref(false);

const isError = computed<boolean>(() => !!errorMessage.value);
const isFileSet = computed<boolean>(() => !!file.value);

const outlineColor = computed<string>(() => {
  if (isError.value) {
    return 'outline-color-error';
  }
  if (isFileSet.value) {
    return 'outline-color-success';
  }
  return '';
});

function resetForm() {
  errorMessage.value = undefined;
  file.value = undefined;
  emit('file-set', undefined);
}

function testFile(target: File) {
  if (!allowedFormatsRegex.test(target.name.toLowerCase())) {
    errorMessage.value = 'Falscher Datentyp';
    return;
  }
  file.value = target;
  emit('file-set', target);
}

function onDragover(event: DragEvent) {
  event.preventDefault();
}

function onDrop(event: DragEvent) {
  event.preventDefault();

  const { dataTransfer } = event;

  if (dataTransfer?.files?.[0]) {
    testFile(dataTransfer.files[0]);
  }
}

function uploadFileChanged(event: Event) {
  const [target] = (event.target as HTMLInputElement).files || [];
  if (target) {
    testFile(target);
  }
}

function resetLoading() {
  downloadingOnProcess.value = false;
}

function downloadFile() {
  downloadingOnProcess.value = true;
  emit('download-file');
}

function triggerFileInput() {
  fileInput.value?.click();
}

onMounted(() => {
  if (dropzoneField.value) {
    dropzoneField.value.addEventListener('dragover', onDragover);
    dropzoneField.value.addEventListener('drop', onDrop);
    dropzoneField.value.addEventListener('click', triggerFileInput);
  }
});

onBeforeUnmount(() => {
  if (dropzoneField.value) {
    dropzoneField.value.removeEventListener('dragover', onDragover);
    dropzoneField.value.removeEventListener('drop', onDrop);
    dropzoneField.value.removeEventListener('click', triggerFileInput);
  }
});

defineExpose({ resetLoading });
</script>

<style scoped lang="scss">
  @use '../../assets/sass/variables';

  .dropbox {
    outline: variables.theme-color('primary') 3px dashed;
    background-color: variables.lighten(variables.theme-color('primary'), 62%);
  }
  .outline-color-error {
    outline-color: variables.theme-color('error');
  }
  .outline-color-success {
    outline-color: var(--v-success-darken1);
  }
  .file-name {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .loading-spinner {
    animation: lds-dual-ring 1.2s linear infinite;
  }
  @keyframes lds-dual-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
